/**
 * Custom hooks for downloads
 */
import { useState, useEffect, useCallback } from 'react'
import {
  Release,
  getLatestRelease,
  getAllReleases,
  detectUserPlatform,
  detectUserArch,
} from '../api/downloads'

interface UseLatestReleaseResult {
  release: Release | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch the latest release
 */
export function useLatestRelease(channel?: string): UseLatestReleaseResult {
  const [release, setRelease] = useState<Release | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRelease = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await getLatestRelease(channel)
      if (response.success && response.data) {
        setRelease(response.data)
      } else {
        setError(response.error || 'Failed to load release information')
      }
    } catch {
      setError('Unable to connect. Please check your internet connection.')
    } finally {
      setIsLoading(false)
    }
  }, [channel])

  useEffect(() => {
    fetchRelease()
  }, [fetchRelease])

  return { release, isLoading, error, refetch: fetchRelease }
}

interface UseAllReleasesResult {
  releases: Release[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch all releases
 */
export function useAllReleases(
  channel?: string,
  limit: number = 10
): UseAllReleasesResult {
  const [releases, setReleases] = useState<Release[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchReleases = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await getAllReleases(channel, limit)
      if (response.success && response.data) {
        setReleases(response.data)
      } else {
        setError(response.error || 'Failed to load releases')
      }
    } catch {
      setError('Unable to connect. Please check your internet connection.')
    } finally {
      setIsLoading(false)
    }
  }, [channel, limit])

  useEffect(() => {
    fetchReleases()
  }, [fetchReleases])

  return { releases, isLoading, error, refetch: fetchReleases }
}

interface UserPlatformInfo {
  platform: 'MacOS' | 'Windows' | 'Linux'
  arch: 'X64' | 'Arm64'
}

/**
 * Hook to detect user's platform and architecture
 */
export function useUserPlatform(): UserPlatformInfo {
  const [info] = useState<UserPlatformInfo>(() => ({
    platform: detectUserPlatform(),
    arch: detectUserArch(),
  }))

  return info
}

/**
 * Hook to get the best download for the user's platform
 */
export function useBestDownload(release: Release | null) {
  const { platform, arch } = useUserPlatform()

  if (!release) {
    return null
  }

  // Try exact match first
  let download = release.downloads.find(
    (d) =>
      d.platform.toLowerCase() === platform.toLowerCase() &&
      d.architecture.toLowerCase() === arch.toLowerCase()
  )

  // Fall back to universal for the platform
  if (!download) {
    download = release.downloads.find(
      (d) =>
        d.platform.toLowerCase() === platform.toLowerCase() &&
        d.architecture.toLowerCase() === 'universal'
    )
  }

  // Fall back to any match for the platform
  if (!download) {
    download = release.downloads.find(
      (d) => d.platform.toLowerCase() === platform.toLowerCase()
    )
  }

  return download
}
